import React, { useState, useRef, useEffect } from 'react';
import useDialog from '../hooks/useDialog';
import { createPortal } from 'react-dom';
// The pure entry injects Stripe.js on the first loadStripe call, not on import (265 KB on every page before).
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements } from '@stripe/react-stripe-js';
import { useMonetization } from '../contexts/MonetizationContext';
import { useTranslation } from '../contexts/LanguageContext';
import StripePaymentForm from './StripePaymentForm';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faLightbulb,
    faBan,
    faCheck,
    faSpinner,
    faStar,
    faGem,
    faUndo,
    faArrowLeft,
    faShieldAlt,
    faHeartBroken,
} from '@fortawesome/free-solid-svg-icons';
import { WEB_BANNERS_ENABLED } from './ads/AdBanner';
import './PurchaseModal.css';

// Initialize Stripe lazily per UI language (only if key is configured)
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromises = {};
// Stripe's 'pt' is European Portuguese; our Portuguese is Brazilian.
const STRIPE_LOCALES = { pt: 'pt-BR' };
const stripeLocale = language => STRIPE_LOCALES[language] || language;
function getStripe(language) {
    if (!stripeKey) return null;
    if (!stripePromises[language]) stripePromises[language] = loadStripe(stripeKey, { locale: stripeLocale(language) });
    return stripePromises[language];
}

/**
 * PurchaseModal - Modal for purchasing hint packs and removing ads.
 *
 * Shows all purchase options with Stripe payment integration for web.
 * Handles both Stripe (web) and native IAP (iOS/Android).
 */
function PurchaseModal() {
    const {
        showPurchaseModal,
        closePurchaseModal,
        purchasesEnabled,
        hintBalance,
        adsRemoved,
        purchaseLoading,
        purchaseWithStripe,
        purchaseWithIAP,
        restorePurchases,
        isNativeMobile,
        PURCHASE_OPTIONS,
        streakData,
        buyFreezeWithHints,
        recoverStreakWithHints,
    } = useMonetization();
    const { t, language } = useTranslation();

    const [selectedOption, setSelectedOption] = useState(null);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [error, setError] = useState(null);
    const [restoring, setRestoring] = useState(false);
    const [buyingFreezeWithHints, setBuyingFreezeWithHints] = useState(false);
    const [recoveringStreak, setRecoveringStreak] = useState(false);

    const overlayRef = useRef(null);

    useBodyScrollLock(showPurchaseModal && purchasesEnabled);
    const dialogRef = useDialog(!!(showPurchaseModal && purchasesEnabled), closePurchaseModal);

    useEffect(() => {
        if (showPurchaseModal && overlayRef.current) {
            overlayRef.current.scrollTop = 0;
        }
    }, [showPurchaseModal]);

    if (!showPurchaseModal || !purchasesEnabled) {
        return null;
    }

    const handleSelectOption = (optionId) => {
        setSelectedOption(optionId);
        setError(null);

        if (isNativeMobile()) {
            // Direct IAP purchase for mobile
            handleIAPPurchase(optionId);
        } else {
            // Show Stripe payment form for web
            setShowPaymentForm(true);
        }
    };

    const handleIAPPurchase = async (optionId) => {
        try {
            const option = PURCHASE_OPTIONS[optionId];
            const result = await purchaseWithIAP(option.productId);

            if (!result.success) {
                setError(result.error || t('store.errors.purchaseFailed'));
            }
        } catch (err) {
            setError(err.message || t('store.errors.generic'));
        } finally {
            setSelectedOption(null);
        }
    };

    const handleStripePayment = async (stripe, elements, cardElement) => {
        try {
            // Call the context's purchaseWithStripe function
            const result = await purchaseWithStripe(selectedOption, stripe, elements, cardElement);

            if (result.success) {
                // Success! Close the modal
                setShowPaymentForm(false);
                setSelectedOption(null);
                setError(null);
            } else {
                setError(result.error || t('store.errors.paymentFailed'));
            }
        } catch (err) {
            setError(err.message || t('store.errors.paymentError'));
            throw err;
        }
    };

    const handleCancelPayment = () => {
        setShowPaymentForm(false);
        setSelectedOption(null);
        setError(null);
    };

    const handleRestore = async () => {
        setRestoring(true);
        setError(null);

        try {
            const result = await restorePurchases();
            if (result.success) {
                if (result.restored && result.restored.length > 0) {
                    setError(null);
                } else {
                    setError(t('store.errors.nothingToRestore'));
                }
            } else {
                setError(result.error || t('store.errors.restoreFailed'));
            }
        } catch (err) {
            setError(err.message || t('store.errors.restoreFailed'));
        } finally {
            setRestoring(false);
        }
    };

    const handleBuyFreezeWithHints = async (count) => {
        setBuyingFreezeWithHints(true);
        setError(null);
        try {
            const result = await buyFreezeWithHints(count);
            if (!result.success) {
                setError(result.error || t('store.errors.freezeFailed'));
            }
        } catch (err) {
            setError(err.message || t('store.errors.freezeFailed'));
        } finally {
            setBuyingFreezeWithHints(false);
        }
    };

    const handleRecoverWithHints = async () => {
        setRecoveringStreak(true);
        setError(null);
        try {
            const result = await recoverStreakWithHints();
            if (!result.success) {
                setError(result.error || t('store.errors.recoveryFailed'));
            }
        } catch (err) {
            setError(err.message || t('store.errors.recoveryFailed'));
        } finally {
            setRecoveringStreak(false);
        }
    };

    const handleClose = () => {
        if (!purchaseLoading) {
            setShowPaymentForm(false);
            setSelectedOption(null);
            setError(null);
            closePurchaseModal();
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const hintOptions = [
        { ...PURCHASE_OPTIONS.hints_10, icon: faLightbulb },
        { ...PURCHASE_OPTIONS.hints_25, icon: faStar },
        { ...PURCHASE_OPTIONS.hints_50, icon: faGem },
    ];

    const selectedPurchase = selectedOption ? PURCHASE_OPTIONS[selectedOption] : null;

    return createPortal(
        <div className="modal-overlay-base purchase-overlay" ref={overlayRef} onClick={handleOverlayClick}>
            <div className="modal-content-base purchase-modal" data-modal-scrollable ref={dialogRef} role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
                {!purchaseLoading && !showPaymentForm && (
                    <button
                        className="purchase-close"
                        onClick={handleClose}
                        aria-label={t('common.close')}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}

                {showPaymentForm && selectedPurchase ? (
                    // Stripe Payment Form View
                    <div className="payment-form-view">
                        <button
                            className="back-button"
                            onClick={handleCancelPayment}
                            disabled={purchaseLoading}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            <span>{t('common.back')}</span>
                        </button>

                        <div className="purchase-header">
                            <h2>{t('store.completePurchase')}</h2>
                            <p className="purchase-item-name">{selectedPurchase.name}</p>
                        </div>

                        {error && (
                            <div className="purchase-error">
                                {error}
                            </div>
                        )}

                        <Elements key={language} stripe={getStripe(language)} options={{ locale: stripeLocale(language) }}>
                            <StripePaymentForm
                                purchaseType={selectedOption}
                                amount={Math.round(selectedPurchase.priceAmount * 100)}
                                onSuccess={handleStripePayment}
                                onError={(err) => setError(err.message)}
                                onCancel={handleCancelPayment}
                                loading={purchaseLoading}
                            />
                        </Elements>
                    </div>
                ) : (
                    // Purchase Options View
                    <>
                        {purchaseLoading && (
                            <div className="purchase-loading-overlay">
                                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                                <p>{t('store.processingPurchase')}</p>
                            </div>
                        )}

                        <div className="purchase-header">
                            <h2>{t('store.title')}</h2>
                            <p className="purchase-balance">
                                {t.rich('store.balance', { b: <strong /> }, { count: hintBalance })}
                            </p>
                        </div>

                        {error && (
                            <div className="purchase-error">
                                {error}
                            </div>
                        )}

                        {/* Hint Packs */}
                        <div className="purchase-section">
                            <h3>{t('store.sections.hintPacks')}</h3>
                            <div className="purchase-options">
                                {hintOptions.map((option) => (
                                    <button
                                        key={option.id}
                                        className={`purchase-option ${option.badge ? 'has-badge' : ''}`}
                                        onClick={() => handleSelectOption(option.id)}
                                        disabled={purchaseLoading}
                                    >
                                        {option.badge && (
                                            <span className="option-badge">{option.badge}</span>
                                        )}
                                        <div className="option-icon">
                                            <FontAwesomeIcon icon={option.icon} />
                                        </div>
                                        <div className="option-details">
                                            <span className="option-name">{option.name}</span>
                                            <span className="option-price">{option.price}</span>
                                        </div>
                                        <FontAwesomeIcon icon={faCheck} className="option-check" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Remove Ads: only where ads actually show */}
                        {!adsRemoved && (isNativeMobile() || WEB_BANNERS_ENABLED) && (
                            <div className="purchase-section">
                                <h3>{t('store.sections.removeAds')}</h3>
                                <button
                                    className="purchase-option remove-ads-option"
                                    onClick={() => handleSelectOption('remove_ads')}
                                    disabled={purchaseLoading}
                                >
                                    <div className="option-icon remove-ads-icon">
                                        <FontAwesomeIcon icon={faBan} />
                                    </div>
                                    <div className="option-details">
                                        <span className="option-name">{PURCHASE_OPTIONS.remove_ads.name}</span>
                                        <span className="option-description">
                                            {PURCHASE_OPTIONS.remove_ads.description}
                                        </span>
                                        <span className="option-price">{PURCHASE_OPTIONS.remove_ads.price}</span>
                                    </div>
                                    <FontAwesomeIcon icon={faCheck} className="option-check" />
                                </button>
                            </div>
                        )}

                        {adsRemoved && (isNativeMobile() || WEB_BANNERS_ENABLED) && (
                            <div className="purchase-section ads-removed-banner">
                                <FontAwesomeIcon icon={faCheck} />
                                <span>{t('store.adsRemoved')}</span>
                            </div>
                        )}

                        {/* Streak Freezes */}
                        <div className="purchase-section">
                            <h3>
                                <FontAwesomeIcon icon={faShieldAlt} className="section-icon-freeze" />
                                {' '}{t('store.sections.freezes')}
                            </h3>
                            {streakData?.freezeCount > 0 && (
                                <p className="freeze-balance">
                                    {t.rich('store.freezeBalance', { b: <strong /> }, { count: streakData.freezeCount })}
                                </p>
                            )}
                            <div className="purchase-options">
                                <button
                                    className="purchase-option"
                                    onClick={() => handleSelectOption('streak_freeze_1')}
                                    disabled={purchaseLoading}
                                >
                                    <div className="option-icon">
                                        <FontAwesomeIcon icon={faShieldAlt} />
                                    </div>
                                    <div className="option-details">
                                        <span className="option-name">{PURCHASE_OPTIONS.streak_freeze_1.name}</span>
                                        <span className="option-price">{PURCHASE_OPTIONS.streak_freeze_1.price}</span>
                                    </div>
                                    <FontAwesomeIcon icon={faCheck} className="option-check" />
                                </button>
                                <button
                                    className={`purchase-option ${PURCHASE_OPTIONS.streak_freeze_3.badge ? 'has-badge' : ''}`}
                                    onClick={() => handleSelectOption('streak_freeze_3')}
                                    disabled={purchaseLoading}
                                >
                                    {PURCHASE_OPTIONS.streak_freeze_3.badge && (
                                        <span className="option-badge">{PURCHASE_OPTIONS.streak_freeze_3.badge}</span>
                                    )}
                                    <div className="option-icon">
                                        <FontAwesomeIcon icon={faShieldAlt} />
                                    </div>
                                    <div className="option-details">
                                        <span className="option-name">{PURCHASE_OPTIONS.streak_freeze_3.name}</span>
                                        <span className="option-price">{PURCHASE_OPTIONS.streak_freeze_3.price}</span>
                                    </div>
                                    <FontAwesomeIcon icon={faCheck} className="option-check" />
                                </button>
                            </div>
                            {hintBalance >= 5 && (
                                <button
                                    className="buy-freeze-hints-button"
                                    onClick={() => handleBuyFreezeWithHints(1)}
                                    disabled={buyingFreezeWithHints || purchaseLoading}
                                >
                                    {buyingFreezeWithHints ? (
                                        <><FontAwesomeIcon icon={faSpinner} spin /> {t('store.buying')}</>
                                    ) : (
                                        <>{t('store.buyFreezeWithHints', { cost: 5 })}</>
                                    )}
                                </button>
                            )}
                        </div>

                        {/* Streak Recovery (only when recovery available) */}
                        {streakData?.recovery?.available && (
                            <div className="purchase-section recovery-section">
                                <h3>
                                    <FontAwesomeIcon icon={faHeartBroken} className="section-icon-recovery" />
                                    {' '}{t('store.sections.recovery')}
                                </h3>
                                <p className="recovery-info">
                                    {t.rich('store.recovery.info', { b: <strong /> }, {
                                        streak: t('streak.streakLength', { count: streakData.recovery.lostStreak }),
                                        missed: t('common.count.days', { count: streakData.recovery.daysMissed }),
                                    })}
                                </p>
                                <div className="purchase-options">
                                    <button
                                        className="purchase-option recovery-option"
                                        onClick={() => handleSelectOption('streak_recovery')}
                                        disabled={purchaseLoading}
                                    >
                                        <div className="option-icon recovery-icon">
                                            <FontAwesomeIcon icon={faHeartBroken} />
                                        </div>
                                        <div className="option-details">
                                            <span className="option-name">{t('store.recovery.recoverStreak')}</span>
                                            <span className="option-price">{PURCHASE_OPTIONS.streak_recovery.price}</span>
                                        </div>
                                        <FontAwesomeIcon icon={faCheck} className="option-check" />
                                    </button>
                                </div>
                                {hintBalance >= streakData.recovery.costHints && (
                                    <button
                                        className="buy-freeze-hints-button"
                                        onClick={handleRecoverWithHints}
                                        disabled={recoveringStreak || purchaseLoading}
                                    >
                                        {recoveringStreak ? (
                                            <><FontAwesomeIcon icon={faSpinner} spin /> {t('store.recovery.recovering')}</>
                                        ) : (
                                            <>{t('store.recovery.withHints', { count: streakData.recovery.costHints })}</>
                                        )}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Restore Purchases (mainly for iOS) */}
                        {isNativeMobile() && (
                            <button
                                className="restore-button"
                                onClick={handleRestore}
                                disabled={restoring || purchaseLoading}
                            >
                                {restoring ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        <span>{t('store.restoring')}</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faUndo} />
                                        <span>{t('store.restore')}</span>
                                    </>
                                )}
                            </button>
                        )}

                        <p className="purchase-note">
                            {t('store.note')}
                        </p>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}

export default PurchaseModal;
